import { Copy } from 'lucide-react';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { type DecisionSheetView } from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import { copyText } from '../../../lib/clipboard';
import { decisionResultText, type DecisionTotals } from '../model';

/**
 * Thanh tổng ba ô **bằng nhau** (plan §2, §7): Đã chốt n/N + thanh tiến độ ·
 * Chọn đúng đề xuất · Còn chờ. Ô xếp lưới ba cột ở mọi bề rộng — ở 390px mỗi
 * ô ~110px, đủ cho một con số và nhãn hai dòng; nhãn giữ chỗ hai dòng ở cả ba
 * ô để các số đứng thẳng hàng.
 */
export function DecisionTotalsBar({ totals }: { readonly totals: DecisionTotals }): ReactNode {
  const { t } = useTranslation();
  const percent = totals.total === 0 ? 0 : Math.round((totals.answered / totals.total) * 100);

  return (
    <section aria-label={t('decisionSheet.totals.label')} className="grid grid-cols-3 gap-2">
      <Tile label={t('decisionSheet.totals.answered')} testId="decision-total-answered">
        <span>
          {totals.answered}
          <span className="text-base font-normal text-muted-foreground"> / {totals.total}</span>
        </span>
        <span
          className="mt-1 block h-1.5 w-full overflow-hidden rounded-full bg-muted"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={totals.total}
          aria-valuenow={totals.answered}
        >
          <span className="block h-full rounded-full bg-primary" style={{ width: `${percent}%` }} />
        </span>
      </Tile>
      <Tile label={t('decisionSheet.totals.recommended')} testId="decision-total-recommended">
        {totals.recommended}
      </Tile>
      <Tile label={t('decisionSheet.totals.pending')} testId="decision-total-pending">
        {totals.pending}
      </Tile>
    </section>
  );
}

function Tile({
  label,
  testId,
  children,
}: {
  readonly label: string;
  readonly testId: string;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <div
      data-testid={testId}
      className="flex min-w-0 flex-col justify-between gap-1 rounded-DEFAULT border border-border bg-card p-3 max-sm:p-2"
    >
      <span className="line-clamp-2 min-h-8 text-xs leading-4 text-muted-foreground">{label}</span>
      <span className="text-2xl font-semibold tabular-nums max-sm:text-xl">{children}</span>
    </div>
  );
}

/** "Kết quả đã chốt" — văn bản thuần chép được, cuối trang (plan §2). */
export function DecisionResult({
  view,
  id,
}: {
  readonly view: Pick<DecisionSheetView, 'title' | 'groups' | 'questions'>;
  readonly id: string;
}): ReactNode {
  const { t } = useTranslation();
  const text = decisionResultText(view, {
    other: t('decisionSheet.other'),
    matchesRecommended: t('decisionSheet.result.matchesRecommended'),
    unanswered: t('decisionSheet.result.unanswered'),
    note: t('decisionSheet.result.note'),
    ungrouped: t('decisionSheet.ungrouped'),
  });

  return (
    <section id={id} className="flex scroll-mt-16 flex-col gap-2 border-t border-border pt-4">
      <div className="flex items-center gap-2">
        <h3 className="text-base font-semibold">{t('decisionSheet.resultTitle')}</h3>
        <Button
          size="sm"
          variant="outline"
          className="ml-auto"
          onClick={() =>
            void copyText(text).then((ok) =>
              ok
                ? toast.success(t('decisionSheet.resultCopied'))
                : toast.error(t('decisionSheet.resultCopyFailed')),
            )
          }
        >
          <Copy aria-hidden />
          {t('decisionSheet.resultCopy')}
        </Button>
      </div>
      <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded-DEFAULT border border-border bg-muted/30 p-3 font-sans text-sm [overflow-wrap:anywhere]">
        {text}
      </pre>
    </section>
  );
}
